// The conventional no-op href used to render a link as inert (e.g. for a
// disabled state implemented by consumers, or third-party code). Allowed
// through even though its scheme matches the pattern below.
// eslint-disable-next-line no-script-url -- this is the safe no-op fallback value, not a navigation.
const NOOP_HREF = 'javascript:void(0)';

// Matches the scheme portion of an absolute URL, e.g. "javascript:" in
// "javascript:alert(1)".
const PROTOCOL_REGEX = /^([^:]+):/;

// Whitespace and control characters are ignored by browsers when parsing a
// URL scheme, so they must be stripped before comparing — otherwise an
// obfuscated payload like "java\tscript:" or "java\x00script:" slips through.
// eslint-disable-next-line no-control-regex
const IGNORED_PROTOCOL_CHARS_REGEX = /[\s\x00-\x1f]/g;

/**
 * Detects `javascript:` URIs, including obfuscated variants (mixed case,
 * embedded whitespace/control characters), so they can be blocked before
 * reaching an `href`. `javascript:void(0)` is allowed through, since it's
 * the conventional no-op href used to render a link as inert.
 *
 * Ports HubSpot's established `isUnsafeUrl` implementation (UIComponents /
 * foundations-components) so Trellis primitives guard against the same
 * known XSS vector, rather than forwarding an arbitrary `href` straight to
 * the DOM.
 */
export function isUnsafeUrl(href: string | undefined): boolean {
  if (!href) return false;

  const protocolMatch = href.match(PROTOCOL_REGEX);
  if (!protocolMatch || href === NOOP_HREF) return false;

  return (
    protocolMatch[0].replace(IGNORED_PROTOCOL_CHARS_REGEX, '').toLowerCase() ===
    // eslint-disable-next-line no-script-url -- comparison target for detection, not a navigation.
    'javascript:'
  );
}

/**
 * Returns `href` unchanged, or the safe no-op `javascript:void(0)` when
 * `href` is an unsafe `javascript:` URI. Apply this immediately before
 * rendering a consumer-provided `href` onto a DOM element.
 */
export function getSafeUrl(href: string | undefined): string | undefined {
  return isUnsafeUrl(href) ? NOOP_HREF : href;
}
